import { useEffect, useRef } from 'react';

type AutoSaveDesignOptions = {
  ready: boolean;
  dirty: boolean;
  revision: number;
  stale: boolean;
  pending: boolean;
  fingerprint: string;
  save: () => Promise<unknown>;
};

/** Debounce edits, retaining failed attempts until the state or base revision changes. */
export const useAutoSaveDesign = ({
  ready,
  dirty,
  revision,
  stale,
  pending,
  fingerprint,
  save,
}: AutoSaveDesignOptions) => {
  const lastAttempt = useRef<{ revision: number; fingerprint: string } | null>(
    null
  );

  useEffect(() => {
    // A different analysis needs an explicit replacement decision, even if
    // the user has edited its generated defaults.
    if (!ready || stale || pending || (!dirty && revision !== 0))
      return undefined;
    if (
      lastAttempt.current?.revision === revision &&
      lastAttempt.current.fingerprint === fingerprint
    )
      return undefined;

    const timeout = window.setTimeout(() => {
      lastAttempt.current = { revision, fingerprint };
      // The caller surfaces save failures and provides the manual retry action.
      void save().catch(() => undefined);
    }, 750);
    return () => window.clearTimeout(timeout);
  }, [ready, dirty, revision, stale, pending, fingerprint, save]);
};
